import React from "react";
import { useTranslation } from "react-i18next";
import {
  Document,
  ArrowRightLong,
  TooltipIcon,
} from "@/components/icons";
import Tooltip from "@/components/ui/tooltip";
import type { ClientPollStatus } from "../../types/client-polls.types";
import styles from "./PollCard.module.scss";

export interface PollCardProps {
  title: string;
  startDate: string;
  endDate: string;
  status: ClientPollStatus;
  onAction?: () => void;
  className?: string;
}

const PollCard: React.FC<PollCardProps> = ({
  title,
  startDate,
  endDate,
  status,
  onAction,
  className = "",
}) => {
  const { t } = useTranslation();

  const renderFooter = () => {
    if (status === "pending") {
      return (
        <button
          type="button"
          className={styles.pollCard__action}
          onClick={(e) => {
            e.stopPropagation();
            onAction?.();
          }}
        >
          <span className={styles.pollCard__actionText}>
            {t("clientPolls.card.answer")}
          </span>
          <ArrowRightLong width={20} height={20} color="#e7212b" />
        </button>
      );
    }
    if (status === "answered") {
      return (
        <button
          type="button"
          className={styles.pollCard__action}
          onClick={(e) => {
            e.stopPropagation();
            onAction?.();
          }}
        >
          <span className={styles.pollCard__actionText}>
            {t("clientPolls.card.viewAnswers")}
          </span>
          <ArrowRightLong width={20} height={20} color="#e7212b" />
        </button>
      );
    }
    // expired
    return (
      <Tooltip
        content={t("clientPolls.card.expiredTooltip")}
        position="top"
      >
        <div className={styles.pollCard__statusExpired}>
          <span>{t("clientPolls.card.expired")}</span>
          <TooltipIcon width={16} height={16} />
        </div>
      </Tooltip>
    );
  };

  return (
    <div
      className={`${styles.pollCard} ${className}`}
      role={status === "pending" || status === "answered" ? "button" : undefined}
      tabIndex={status === "pending" || status === "answered" ? 0 : undefined}
      onClick={status === "pending" || status === "answered" ? onAction : undefined}
      onKeyDown={
        status === "pending" || status === "answered"
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onAction?.();
              }
            }
          : undefined
      }
    >
      <div className={styles.pollCard__header}>
        <Document width={24} height={24} className={styles.pollCard__icon} />
        <span className={styles.pollCard__title}>{title}</span>
      </div>
      <div className={styles.pollCard__dates}>
        <div className={styles.pollCard__dateRow}>
          <span className={styles.pollCard__dateLabel}>
            {t("clientPolls.card.startDate")}
          </span>
          <span className={styles.pollCard__dateValue}>{startDate}</span>
        </div>
        <div className={styles.pollCard__dateRow}>
          <span className={styles.pollCard__dateLabel}>
            {t("clientPolls.card.endDate")}
          </span>
          <span className={styles.pollCard__dateValue}>{endDate}</span>
        </div>
      </div>
      <div className={styles.pollCard__footer}>{renderFooter()}</div>
    </div>
  );
};

export default PollCard;
